import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Photo viewer modal with full-screen preview
 * @param {boolean} isOpen - Whether the modal is open
 * @param {string} photoUrl - URL of the photo to display
 * @param {string} caption - Optional caption for the photo
 * @param {function} onClose - Callback when user closes the modal
 * @param {array} allPhotos - Optional array of all photos for navigation
 * @param {number} currentIndex - Current photo index in allPhotos array
 * @param {function} onNavigate - Callback when user navigates (prev/next)
 */
export default function PhotoModal({
  isOpen,
  photoUrl,
  caption,
  onClose,
  allPhotos = null,
  currentIndex = 0,
  onNavigate = null
}) {
  if (!isOpen) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && onNavigate && currentIndex > 0) {
      onNavigate(currentIndex - 1);
    } else if (e.key === 'ArrowRight' && onNavigate && allPhotos && currentIndex < allPhotos.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, allPhotos]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors z-10"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Navigation buttons (if multiple photos) */}
      {allPhotos && allPhotos.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={() => onNavigate(currentIndex - 1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-slate-300 transition-colors bg-black/50 rounded-full p-2 z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {currentIndex < allPhotos.length - 1 && (
            <button
              onClick={() => onNavigate(currentIndex + 1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-slate-300 transition-colors bg-black/50 rounded-full p-2 z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
        </>
      )}

      {/* Photo counter */}
      {allPhotos && allPhotos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
          {currentIndex + 1} / {allPhotos.length}
        </div>
      )}

      {/* Photo */}
      <div className="relative max-w-7xl max-h-[90vh] flex flex-col items-center">
        <img
          src={photoUrl}
          alt={caption || 'Zdjęcie'}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Caption */}
        {caption && (
          <div className="mt-4 text-white text-center bg-black/50 px-6 py-3 rounded-lg max-w-2xl">
            {caption}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        {allPhotos && allPhotos.length > 1 ? (
          <span>Użyj strzałek lub kliknij, aby nawigować • ESC aby zamknąć</span>
        ) : (
          <span>Kliknij poza zdjęcie lub naciśnij ESC aby zamknąć</span>
        )}
      </div>
    </div>
  );
}
